import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Star,
  Plus,
  X,
  TrendingUp,
  TrendingDown,
  Bell,
  BellOff,
  AlertCircle,
  Send,
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { toast } from "sonner";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NIFTY50_OPTIONS = [
  "RELIANCE", "TCS", "HDFCBANK", "ICICIBANK", "INFY",
  "HINDUNILVR", "ITC", "SBIN", "BHARTIARTL", "KOTAKBANK",
  "LT", "AXISBANK", "BAJFINANCE", "ASIANPAINT", "MARUTI",
  "HCLTECH", "WIPRO", "SUNPHARMA", "TATAMOTORS", "M&M",
  "TITAN", "ULTRACEMCO", "POWERGRID", "NTPC", "ADANIPORTS",
  "TATASTEEL", "JSWSTEEL", "ONGC", "TECHM", "BAJAJFINSV",
];

const WatchlistRow = ({ item, onRemove }) => {
  const isBull = item.direction === "BULLISH";
  const isBear = item.direction === "BEARISH";
  const dirColor = isBull
    ? "var(--bull)"
    : isBear
    ? "var(--bear)"
    : "var(--text-secondary)";
  const dirBg = isBull
    ? "var(--bull-muted)"
    : isBear
    ? "var(--bear-muted)"
    : "rgba(255,255,255,0.04)";
  const Icon = isBull ? TrendingUp : isBear ? TrendingDown : Star;

  return (
    <div
      data-testid={`watchlist-row-${item.symbol.replace(".NS", "")}`}
      className={`border-grid surface px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 ${
        item.alert ? "ring-1" : ""
      }`}
      style={item.alert ? { boxShadow: `inset 0 0 0 1px ${dirColor}` } : {}}
    >
      <div className="flex items-center gap-3 min-w-[200px]">
        <div
          className="w-8 h-8 flex items-center justify-center"
          style={{ background: dirBg, color: dirColor, border: `1px solid ${dirColor}` }}
        >
          <Icon size={14} />
        </div>
        <div>
          <div className="font-heading font-semibold text-sm leading-tight">
            {item.name}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            {item.symbol.replace(".NS", "")}
          </div>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            Price
          </div>
          <div className="font-mono-data text-sm font-semibold">
            ₹{item.price.toFixed(2)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            Direction
          </div>
          <div className="font-mono-data text-sm font-semibold" style={{ color: dirColor }}>
            {item.direction}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            Entry / SL / Tgt
          </div>
          <div className="font-mono-data text-xs">
            {item.entry.toFixed(2)} /{" "}
            <span style={{ color: "var(--bear)" }}>
              {item.stop_loss.toFixed(2)}
            </span>{" "}
            /{" "}
            <span style={{ color: "var(--bull)" }}>
              {item.target.toFixed(2)}
            </span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            RSI / Score
          </div>
          <div className="font-mono-data text-sm">
            {item.rsi.toFixed(1)} ·{" "}
            <span style={{ color: dirColor }}>{item.score}</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            Option
          </div>
          <div className="font-mono-data text-xs flex items-center gap-1.5">
            <span>
              {item.option.atm_strike} {item.option.type}
            </span>
            {item.option.atm?.ltp != null && (
              <span className="text-muted-2">@ ₹{item.option.atm.ltp}</span>
            )}
            <span
              className="text-[8px] uppercase tracking-wider px-1 py-0.5 border"
              style={{
                color:
                  (item.option.source === "upstox" || item.option.source === "nse") ? "var(--bull)" : "var(--text-muted)",
                borderColor:
                  (item.option.source === "upstox" || item.option.source === "nse") ? "var(--bull)" : "var(--border-color)",
              }}
            >
              {item.option.source === "upstox" ? "LIVE" : item.option.source === "nse" ? "LIVE" : "EST"}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {item.alert && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-mono-data border"
            style={{
              color: dirColor,
              background: dirBg,
              borderColor: dirColor,
            }}
          >
            <Bell size={11} />
            ALERT
          </span>
        )}
        <button
          data-testid={`watchlist-remove-${item.symbol.replace(".NS", "")}`}
          onClick={() => onRemove(item.symbol)}
          className="border-grid surface p-1.5 hover:bg-white/5 transition-colors"
          aria-label="Remove from watchlist"
        >
          <X size={14} className="text-muted-2" />
        </button>
      </div>
    </div>
  );
};

export const Watchlist = ({ liveItems }) => {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);

  // Sync from WebSocket if available, otherwise initial fetch
  useEffect(() => {
    if (liveItems !== undefined && liveItems !== null) {
      setItems(liveItems);
    }
  }, [liveItems]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/watchlist`);
      setItems(res.data.items || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async (symbol) => {
    if (!symbol) return;
    setBusy(true);
    try {
      await axios.post(`${API}/watchlist`, { symbol });
      toast.success(`${symbol} added to watchlist`);
      setAdding("");
      await refresh();
    } catch (e) {
      toast.error("Failed to add stock");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (symbol) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/watchlist/${symbol}`);
      toast.success("Removed from watchlist");
      await refresh();
    } catch (e) {
      toast.error("Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  const handleTestAlert = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/alerts/test`);
      toast.success("Telegram test alert sent — check your phone 📱");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Test failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const [alertsPaused, setAlertsPaused] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/alerts/status`)
      .then((r) => setAlertsPaused(!!r.data?.paused))
      .catch(() => {});
  }, []);

  const togglePause = async () => {
    setBusy(true);
    try {
      const next = !alertsPaused;
      await axios.post(`${API}/alerts/pause`, null, {
        params: { paused: next ? "true" : "false" },
      });
      setAlertsPaused(next);
      toast.success(next ? "Telegram alerts paused" : "Telegram alerts resumed");
    } catch (e) {
      toast.error("Failed to update alert status");
    } finally {
      setBusy(false);
    }
  };

  const alertCount = (items || []).filter((i) => i.alert).length;
  const [collapsed, toggle] = useCollapsed("watchlist");

  return (
    <section data-testid="watchlist-section" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-muted-3 font-mono-data uppercase">
            03 / Watchlist
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold mt-1">
            My Watchlist
          </h2>
          <p className="text-xs text-muted-3 mt-1">
            Live-tracked favourites · alerts when trade setup qualifies (score ≥ 50)
          </p>
        </div>
        {alertCount > 0 && (
          <div
            data-testid="watchlist-alert-count"
            className="inline-flex items-center gap-2 px-3 py-1.5 border font-mono-data text-xs uppercase tracking-wider"
            style={{
              color: "var(--warn)",
              borderColor: "var(--warn)",
              background: "rgba(255,159,10,0.08)",
            }}
          >
            <AlertCircle size={12} />
            {alertCount} active alert{alertCount > 1 ? "s" : ""}
          </div>
        )}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={toggle}
          testId="watchlist-toggle"
        />
      </div>

      {!collapsed && (<>
      {/* Add stock control */}
      <div className="border-grid surface p-3 flex flex-col md:flex-row gap-2 md:items-center">
        <span className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data px-1">
          Add Stock
        </span>
        <select
          data-testid="watchlist-symbol-select"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          className="bg-transparent border-grid px-3 py-2 text-sm font-mono-data flex-1 outline-none focus:ring-1"
          style={{ color: "var(--text-primary)" }}
        >
          <option value="">— Select Nifty 50 stock —</option>
          {NIFTY50_OPTIONS.map((s) => (
            <option key={s} value={s} style={{ background: "#0a0a0a" }}>
              {s}
            </option>
          ))}
        </select>
        <button
          data-testid="watchlist-add-button"
          onClick={() => handleAdd(adding)}
          disabled={!adding || busy}
          className="border-grid px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-wider font-mono-data hover:bg-white/5 transition-colors disabled:opacity-40"
          style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
        >
          <Plus size={14} />
          Add
        </button>
        <button
          data-testid="watchlist-test-alert-button"
          onClick={handleTestAlert}
          disabled={busy}
          className="border-grid px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-wider font-mono-data hover:bg-white/5 transition-colors disabled:opacity-40"
          style={{ color: "var(--warn)", borderColor: "var(--warn)" }}
        >
          <Send size={14} />
          Test Telegram
        </button>
        <button
          data-testid="watchlist-pause-alerts-button"
          onClick={togglePause}
          disabled={busy}
          className="border-grid px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-wider font-mono-data hover:bg-white/5 transition-colors disabled:opacity-40"
          style={{
            color: alertsPaused ? "var(--bear)" : "var(--text-secondary)",
            borderColor: alertsPaused ? "var(--bear)" : "var(--border-color)",
            background: alertsPaused ? "var(--bear-muted)" : "transparent",
          }}
        >
          {alertsPaused ? <BellOff size={14} /> : <Bell size={14} />}
          {alertsPaused ? "Alerts Paused" : "Pause Alerts"}
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {items === null ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="border-grid surface p-4">
              <Skeleton className="h-12 w-full" />
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="border-grid surface p-8 text-center">
            <Star size={24} className="mx-auto mb-2 text-muted-3" />
            <p className="text-sm text-muted-2">
              Your watchlist is empty. Add a Nifty 50 stock to track it live and
              receive an alert the moment a high-conviction setup forms.
            </p>
          </div>
        ) : (
          items.map((item) => (
            <WatchlistRow
              key={item.symbol}
              item={item}
              onRemove={handleRemove}
            />
          ))
        )}
      </div>
        </>
      )}
    </section>
  );
};
